import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  deleteSegmentation,
  getAzureBlob,
  propagateCategoryChange,
  updateOutputSurface,
} from "../services/container";
import { getOutputSurfaceBlobNameFromImageUrl } from "../shared/utils/utils";
import { formattedColorMaterial } from "./formatted/FormattedText";

async function handleObjectSegmentation(
  review,
  data,
  captureTime,
  type,
  setLoading,
) {
  setLoading(true);
  try {
    const containerId = review.containerId ?? data._measurement ?? data._id;
    const canonicalBlob = getOutputSurfaceBlobNameFromImageUrl(
      review.image_url,
      containerId,
    );
    // Prefer the blob that carried the revision metadata (legacy vs canonical).
    const outputSurfaceBlob = review.sourceBlobName || canonicalBlob;
    if (!outputSurfaceBlob) {
      throw new Error("Unable to resolve output-surface blob");
    }

    const outputSurfaceData = await getAzureBlob({
      blobUrl: `${process.env.REACT_APP_AZURE_URL}${outputSurfaceBlob}`,
    });

    if (!outputSurfaceData || outputSurfaceData.status !== 200) {
      console.warn("Blob not found or error fetching:", outputSurfaceBlob);
      return false;
    }

    const wasteObjectsList = outputSurfaceData.data.object_segmentations || [];
    const isDeletionRequest =
      review.reviewRequestType === "delete" ||
      review.suggestedForDeletion === true ||
      Boolean(review.deleteSuggestionComment);

    let updatedSourceObject = null;
    const updatedList = wasteObjectsList.map((item) => {
      if (item.id !== review.id) return item;

      if (type === "accepted" && isDeletionRequest) {
        return {
          ...item,
          reviewStatus: type,
          deletionAccepted: true,
          suggestedForDeletion: true,
          deleteSuggestionComment:
            review.deleteSuggestionComment || item.deleteSuggestionComment,
        };
      }

      if (type === "accepted") {
        const isAuthorizedMaterial =
          outputSurfaceData.data.waste_category_authorized_materials?.some(
            (mat) => review.reviewCls === mat.value,
          ) || false;

        updatedSourceObject = {
          ...item,
          reviewCls: null,
          reviewTime: review.reviewTime ?? item.reviewTime,
          reviewRequestType: review.reviewRequestType,
          oldCls: review.oldCls ?? item.cls,
          cls: review.reviewCls,
          originalCls: review.reviewCls,
          color: formattedColorMaterial(review.reviewCls),
          isAuthorizedMaterial,
          reviewStatus: type,
        };
        return updatedSourceObject;
      }

      if (type === "rejected") {
        return {
          ...item,
          reviewCls: review.reviewCls,
          reviewTime: review.reviewTime,
          reviewRequestType: review.reviewRequestType,
          oldCls: review.oldCls ?? item.cls,
          suggestedForDeletion: review.suggestedForDeletion,
          deleteSuggestionComment: review.deleteSuggestionComment,
          reviewStatus: type,
        };
      }

      return item;
    });

    const params = {
      data: {
        ...outputSurfaceData.data,
        anomalies: outputSurfaceData.data.anomalies || 0,
        object_segmentations: updatedList,
      },
      blobName: outputSurfaceBlob,
      captureTime,
    };

    await updateOutputSurface(params);
    if (type === "accepted" && isDeletionRequest) {
      await deleteSegmentation({
        blobName: outputSurfaceBlob,
        segmentationId: review.id,
        captureTime,
      });
    } else if (
      type === "accepted" &&
      !isDeletionRequest &&
      review.reviewCls &&
      updatedSourceObject
    ) {
      try {
        await propagateCategoryChange({
          containerId,
          captureTime,
          sourceObject: updatedSourceObject,
          trackObjectId: updatedSourceObject.trackObjectId,
          firstAppearanceAt: updatedSourceObject.firstAppearanceAt,
          mode: "applied",
          newCls: review.reviewCls,
          color: formattedColorMaterial(review.reviewCls),
          comment: review.comment,
          reviewRequestType: review.reviewRequestType || "category",
          isAuthorizedMaterial: updatedSourceObject.isAuthorizedMaterial,
          skipSource: true,
        });
      } catch (propagateError) {
        console.error("Category propagation failed on accept:", propagateError);
      }
    }
    return true;
  } catch (error) {
    console.error("Error in handleObjectSegmentation:", error);
    return false;
  } finally {
    setLoading(false);
  }
}

export default function StatusPicker({
  value = "pending",
  review,
  data,
  time,
  setLoading,
  isAkanthasAccount,
  viewMode,
  reviewComments,
  setReviewComments,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(value);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const statusOptions = [
    {
      value: "pending",
      label: t("pending"),
      icon: "fas fa-history",
      badgeClass: "char-badge char-badge-progress",
    },
    {
      value: "accepted",
      label: t("Validated"),
      icon: "fas fa-check",
      badgeClass: "char-badge char-badge-validated",
    },
    {
      value: "rejected",
      label: t("rejected"),
      icon: "fas fa-exclamation-triangle",
      badgeClass: "char-badge char-badge-error",
    },
  ];

  // Sync when parent row/review changes (pagination, filter, refetch).
  // Otherwise a recycled picker can keep showing Validé for a pending request.
  useEffect(() => {
    setStatus(value || "pending");
  }, [
    value,
    review?.id,
    review?.captureTime,
    review?.image_url,
    review?.containerId,
    review?.sourceBlobName,
    review?.reviewStatus,
  ]);

  const current =
    statusOptions.find((s) => s.value === status) ||
    statusOptions.find((s) => s.value === "pending");

  const updateMenuPosition = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 180;
      setMenuPosition({
        top: rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - menuWidth - 8),
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        btnRef.current &&
        !btnRef.current.contains(e.target) &&
        !e.target.closest(".status-picker-menu-portal")
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleChangeStatus = async (newStatus) => {
    setOpen(false);

    if (review && data && time) {
      const saved = await handleObjectSegmentation(
        review,
        data,
        time,
        newStatus,
        setLoading,
      );
      if (!saved) return;

      setStatus(newStatus);
      setReviewComments((prev) =>
        prev.map((r) => {
          const sameObject =
            review?.id != null &&
            r?.id != null &&
            String(r.id) === String(review.id) &&
            String(r.containerId ?? "") === String(review.containerId ?? "") &&
            String(r.image_url ?? "") === String(review.image_url ?? "") &&
            String(r.captureTime ?? "") === String(review.captureTime ?? "");
          return sameObject ? { ...r, reviewStatus: newStatus } : r;
        }),
      );
    }
  };

  const openDropdown = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 180;
      setMenuPosition({
        top: rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - menuWidth - 8),
      });
    }
    setOpen((o) => !o);
  };

  return (
    <div className="inline-flex relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          if (isAkanthasAccount && viewMode === "admin") {
            openDropdown();
          }
        }}
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium transition hover:opacity-90 ${current.badgeClass}`}
      >
        <i
          className={`${current.icon} text-xs text-current`}
          aria-hidden="true"
        />
        <span>{current.label}</span>
      </button>

      {open &&
        isAkanthasAccount &&
        viewMode === "admin" &&
        createPortal(
          <div
            className="status-picker-menu-portal"
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 9999,
              width: 180,
              minWidth: 180,
              background: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              padding: 4,
            }}
          >
            {statusOptions.map((opt) => {
              const isActive = opt.value === status;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChangeStatus(opt.value);
                  }}
                  className={`flex w-full items-center gap-2 rounded px-2 py-1 text-sm text-left transition border-0 ${opt.badgeClass} ${isActive ? "ring-2 ring-offset-1 ring-[#5F5BFF]" : "hover:opacity-90"}`}
                >
                  <i
                    className={`${opt.icon} text-xs text-current`}
                    aria-hidden="true"
                  />
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
